import { useMemo, useRef } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileText } from '@/lib/icons';

import {
  RECORD_PAYMENT_OPTIONS,
  RecordPayment,
  RecordPaymentModel,
} from '@/features/payments/payment';
import { useAppSelector } from '@/store/hooks';
import { formatCurrency } from '@/lib/utils';
import { recordPayment } from '@/features/payments/api';
import {
  isAlreadyRecorded,
  markRecordKeySent,
  recordFailureHint,
  resolveRecordKey,
  type RecordFailureHint,
  type RecordKeyState,
} from '@/features/payments/record-idempotency';
import { ButtonSpinner } from '@/components/feedback';
import { getContracts } from '@/features/contracts/api';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatDate } from '@/lib/dates';

export const Route = createFileRoute('/_layout/payments/record')({
  component: RecordPaymentPage,
});

const FAILURE_HINT_KEYS: Record<RecordFailureHint, string> = {
  retrySafe: 'payments.form.retrySafe',
  changeToRetry: 'payments.form.changeToRetry',
};

function RecordPaymentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currency =
    useAppSelector((state) => state.tenant.settings?.currency) ?? 'USD';

  const { data: contractsData, isLoading: contractsLoading } = useQuery({
    queryKey: ['contracts', 'active'],
    queryFn: () => getContracts({ contractStatus: 'active' }),
  });

  const contracts = contractsData?.value || [];
  const hasNoContracts = !contractsLoading && contracts.length === 0;

  const form = useForm<RecordPaymentModel>({
    resolver: zodResolver(RecordPayment),
    defaultValues: {
      amount: 0,
      type: 'installment',
      method: 'cash',
    },
  });

  const watchedContractId = form.watch('contractId');

  const selectedContract = useMemo(
    () => contracts.find((c) => c.id === watchedContractId),
    [contracts, watchedContractId]
  );

  const keyState = useRef<RecordKeyState | null>(null);

  const onSubmit = async (values: RecordPaymentModel) => {
    const { state, isRetry } = resolveRecordKey(keyState.current, values);
    keyState.current = markRecordKeySent(state);
    const attemptStartedAt = Date.now();
    try {
      const result = await recordPayment(values, { idempotencyKey: state.key });
      if (result.succeeded) {
        keyState.current = null;
        const alreadyRecorded = isAlreadyRecorded(
          result,
          isRetry,
          attemptStartedAt
        );
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['payments'] }),
          queryClient.invalidateQueries({ queryKey: ['contracts'] }),
        ]);
        toast({
          title: t('common.success'),
          description: alreadyRecorded
            ? t('payments.form.alreadyRecorded')
            : t('payments.form.recorded'),
        });
        navigate({ to: '/payments' });
      } else {
        toast({
          title: t('common.error'),
          description: result.errors?.[0] ?? t('payments.form.recordFailed'),
          variant: 'destructive',
        });
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : t('payments.form.recordFailed');
      const hint = recordFailureHint(err, values.method === 'cash', isRetry);
      toast({
        title: t('common.error'),
        description: hint
          ? `${message} ${t(FAILURE_HINT_KEYS[hint])}`
          : message,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: '/payments' })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold">{t('payments.form.title')}</h1>
      </div>

      {hasNoContracts && (
        <Card className="border-dashed border-amber-500/40 bg-amber-500/5">
          <CardContent className="flex flex-col items-center justify-center py-10 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/15 mb-3">
              <FileText className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="text-sm font-semibold mb-1">
              No active contracts yet
            </h3>
            <p className="text-xs text-muted-foreground max-w-sm mb-4">
              A contract must exist before you can record a payment against it.
              Approve an application to generate one.
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                onClick={() => navigate({ to: '/applications' })}
              >
                Go to Applications
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Form */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t('payments.form.title')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="contractId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t('payments.form.selectContract')}
                        </FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue
                                placeholder={t('payments.form.selectContract')}
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {contracts.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.contractNumber} - {c.customerName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="amount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('payments.form.amount')}</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            {...field}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                          />
                        </FormControl>
                        {selectedContract && (
                          <p className="text-xs text-muted-foreground">
                            Monthly payment:{' '}
                            {formatCurrency(
                              selectedContract.monthlyPayment,
                              currency
                            )}
                          </p>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="method"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('payments.form.method')}</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          {/* Cash plus the live rails only — see
                              RECORD_PAYMENT_OPTIONS. A dead rail offered here
                              books money against a channel that never
                              collected it. */}
                          <SelectContent>
                            {RECORD_PAYMENT_OPTIONS.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label ?? t(option.labelKey)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="providerTransactionId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('payments.form.reference')}</FormLabel>
                        <FormControl>
                          <Input placeholder="TXN-123456" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="note"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('payments.form.note')}</FormLabel>
                        <FormControl>
                          <Textarea {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="submit"
                      disabled={form.formState.isSubmitting || hasNoContracts}
                    >
                      {form.formState.isSubmitting && (
                        <ButtonSpinner className="mr-2" />
                      )}
                      {t('common.actions.confirm')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate({ to: '/payments' })}
                    >
                      {t('common.actions.cancel')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right: Contract Details */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Contract Details</CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedContract ? (
                    <div className="space-y-3">
                      <InfoRow
                        label="Contract"
                        value={
                          <span className="font-mono">
                            {selectedContract.contractNumber}
                          </span>
                        }
                      />
                      <InfoRow
                        label="Customer"
                        value={selectedContract.customerName}
                      />
                      <InfoRow
                        label="Device"
                        value={selectedContract.deviceName}
                      />
                      <InfoRow
                        label="Total Amount"
                        value={
                          <span className="font-semibold">
                            {formatCurrency(
                              selectedContract.totalAmount,
                              currency
                            )}
                          </span>
                        }
                      />
                      <InfoRow
                        label="Monthly Payment"
                        value={
                          <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                            {formatCurrency(
                              selectedContract.monthlyPayment,
                              currency
                            )}
                          </span>
                        }
                      />
                      <InfoRow
                        label="Status"
                        value={
                          <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30">
                            {selectedContract.status}
                          </Badge>
                        }
                      />
                      {selectedContract.overdueAmount > 0 && (
                        <InfoRow
                          label="Overdue"
                          value={
                            <span className="font-semibold text-destructive">
                              {formatCurrency(
                                selectedContract.overdueAmount,
                                currency
                              )}
                            </span>
                          }
                        />
                      )}
                      {selectedContract.nextPaymentDate && (
                        <InfoRow
                          label="Next Due"
                          value={formatDate(selectedContract.nextPaymentDate)}
                        />
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      Select a contract to see its details
                    </div>
                  )}
                </CardContent>
              </Card>

              {selectedContract && (
                <Card>
                  <CardContent className="pt-6 text-center">
                    <p className="text-sm text-muted-foreground">
                      Suggested Payment
                    </p>
                    <p className="text-3xl font-bold mt-1">
                      {formatCurrency(
                        selectedContract.monthlyPayment,
                        currency
                      )}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() =>
                        form.setValue('amount', selectedContract.monthlyPayment)
                      }
                    >
                      Use Monthly Amount
                    </Button>
                    {selectedContract.overdueAmount > 0 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-2 ml-2"
                        onClick={() =>
                          form.setValue(
                            'amount',
                            selectedContract.overdueAmount
                          )
                        }
                      >
                        Pay Overdue (
                        {formatCurrency(
                          selectedContract.overdueAmount,
                          currency
                        )}
                        )
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-center py-1 border-b border-border/50 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}
